import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { History, LogOut, Menu, TrendingUp, UserRound } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const T = {
  en: {
    tagline: "ready for AI search",
    nav: {
      "/": "Dashboard",
      "/site-audit": "Site Audit",
      "/local-rank": "Local Rank",
      "/meta-generator": "Meta Generator",
      "/schema-generator": "Schema",
      "/robots-checker": "Robots AI",
      "/llms-txt": "llms.txt",
      "/indexnow": "IndexNow",
      "/link-tools": "Link Tools",
    },
    cta: "Audit my site",
    signIn: "Sign in",
    myAudits: "My audits",
    signOut: "Sign out",
    footer: "Rang1 (rang1.ca) - Get your website ready for the AI search revolution: audits, local ranking and AI-readiness tools.",
    footerNote: "Rule-based analysis - verifiable results",
  },
  fr: {
    tagline: "prêt pour la recherche IA",
    nav: {
      "/": "Tableau de bord",
      "/site-audit": "Audit de site",
      "/local-rank": "Rang local",
      "/meta-generator": "Générateur méta",
      "/schema-generator": "Schéma",
      "/robots-checker": "Robots IA",
      "/llms-txt": "llms.txt",
      "/indexnow": "IndexNow",
      "/link-tools": "Liens",
    },
    cta: "Auditer mon site",
    signIn: "Connexion",
    myAudits: "Mes audits",
    signOut: "Déconnexion",
    footer: "Rang1 (rang1.ca) - Préparez votre site à la révolution de la recherche IA : audits, positionnement local et outils de préparation IA.",
    footerNote: "Analyse déterministe - résultats vérifiables",
  },
};

const NAV_PATHS = ["/", "/site-audit", "/local-rank", "/meta-generator", "/schema-generator", "/robots-checker", "/llms-txt", "/indexnow", "/link-tools"];

export default function Layout({ children }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { lang, setLang } = useLang();
  const t = T[lang];

  const LangToggle = ({ className = "" }) => (
    <button
      onClick={() => setLang(lang === "fr" ? "en" : "fr")}
      className={`px-2 py-1.5 rounded-md text-xs font-semibold border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ${className}`}
      aria-label={lang === "fr" ? "Switch to English" : "Passer en français"}
      data-testid="lang-toggle-button"
    >
      {lang === "fr" ? "EN" : "FR"}
    </button>
  );

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="topbar-logo-link">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <TrendingUp className="h-5 w-5" />
            </span>
            <span className="leading-tight">
              <span className="block font-heading font-semibold text-lg tracking-tight">Rang1</span>
              <span className="hidden xl:block text-[10px] text-muted-foreground -mt-0.5">{t.tagline}</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5">
            {NAV_PATHS.map((path) => (
              <Link
                key={path}
                to={path}
                data-testid={`nav-link-${path === "/" ? "dashboard" : path.slice(1)}`}
                className={`px-2.5 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname === path
                    ? "bg-accent text-accent-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`}
              >
                {t.nav[path]}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LangToggle className="hidden sm:inline-flex" />
            <Button asChild className="hidden sm:inline-flex" data-testid="topbar-primary-cta-button">
              <Link to="/site-audit">{t.cta}</Link>
            </Button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Account menu" data-testid="topbar-account-button">
                    <UserRound className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
                    {user.email}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/my-audits" data-testid="topbar-my-audits-link">
                      <History className="h-4 w-4 mr-2" /> {t.myAudits}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={signOut} data-testid="topbar-signout-button">
                    <LogOut className="h-4 w-4 mr-2" /> {t.signOut}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="outline" className="hidden sm:inline-flex" data-testid="topbar-signin-button">
                <Link to="/login">{t.signIn}</Link>
              </Button>
            )}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden" aria-label="Open menu" data-testid="mobile-nav-open-button">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <div className="mt-8 flex flex-col gap-1">
                  {NAV_PATHS.map((path) => (
                    <Link
                      key={path}
                      to={path}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${path === "/" ? "dashboard" : path.slice(1)}`}
                      className={`px-3 py-2.5 rounded-md text-sm font-medium ${
                        location.pathname === path ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted"
                      }`}
                    >
                      {t.nav[path]}
                    </Link>
                  ))}
                  <div className="mt-3 border-t pt-3">
                    <div className="px-3 pb-2">
                      <LangToggle />
                    </div>
                    {user ? (
                      <>
                        <p className="px-3 pb-1 text-xs text-muted-foreground truncate">{user.email}</p>
                        <Link to="/my-audits" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted" data-testid="mobile-nav-my-audits">
                          {t.myAudits}
                        </Link>
                        <button onClick={() => { signOut(); setOpen(false); }} className="w-full text-left px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted" data-testid="mobile-nav-signout">
                          {t.signOut}
                        </button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-md text-sm font-medium text-muted-foreground hover:bg-muted" data-testid="mobile-nav-signin">
                        {t.signIn}
                      </Link>
                    )}
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <WhatsAppButton phone="15145787858" />

      <footer className="border-t mt-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">{t.footer}</p>
          <p className="text-xs text-muted-foreground">{t.footerNote}</p>
        </div>
      </footer>
    </div>
  );
}
